// @flow
/**
 * Defines testable Pet modal fields and validation rules.
 */
import moment from 'moment';
import {
  formatDateDB,
  toMoment,
} from '@fortress-technology-solutions/fortress-component-library/utils';

import { DEFAULT_OPTION_VALUE } from './hooks/usePetTypeOptionsQuery';
import { YES_NO_OPTIONS } from '../../../../constants/selectOptions';
import { isToDateNotSameOrAfterFromDate } from '../../../../utils/date-helpers';
import messages from './messages';
import type { PetBreedOption, PetTypeOption } from '../types';

/** Form values for the Service Animal selection. */
export const SERVICE_ANIMAL_YES = 'yes';
export const SERVICE_ANIMAL_NO = 'no';

// Allow letters, digits, spaces, periods, apostrophes, and hyphens.
export const PET_TAG_PATTERN = /^[a-zA-Z0-9 .'-]*$/;
export const PET_TAG_MAX_LENGTH = 20;

const FULL_WIDTH_ON_MOBILE_HALF_ON_DESKTOP = { xs: 12, md: 6 };

/** Returns true for an unanswered select. */
const isUnselected = (value: mixed): boolean =>
  value === undefined ||
  value === null ||
  value === '' ||
  value === DEFAULT_OPTION_VALUE;

/** Returns true for an unanswered date field. */
export const isBlankDate = (value: mixed): boolean =>
  value === undefined || value === null || value === '';

/** Formats the selected local calendar day for validation. */
const toCalendarDay = (value: mixed): ?string => {
  const parsed = toMoment(value);
  return parsed?.isValid() ? formatDateDB(parsed, false) : null;
};

/** End dates cannot be after the current local date. */
export const isFutureDate = (value: mixed): boolean => {
  const day = toCalendarDay(value);
  return day != null && day > formatDateDB(moment(), false);
};

/**
 * A pet cannot be in the household before the household itself moved in.
 * `moveInDay` is a plain calendar day, so the comparison stays timezone-free.
 */
export const isBeforeMoveIn = (value: mixed, moveInDay: ?string): boolean => {
  if (!moveInDay) return false;
  const day = toCalendarDay(value);
  return day != null && day < moveInDay;
};

export const buildPetFields = ({
  petTypeOptions,
  breedOptions,
  requiresWeight,
  disabled,
  hideDates = false,
  hideEndDate = false,
  lockedHelperText,
  householdMoveInDay,
  formatMessage,
  getValues,
}: {
  petTypeOptions: Array<PetTypeOption>,
  breedOptions: Array<PetBreedOption>,
  requiresWeight: boolean,
  disabled: boolean,
  // Applicant pets do not store Start Date or End Date.
  hideDates?: boolean,
  // A pet being added has no end date to record yet.
  hideEndDate?: boolean,
  // Explains a household-level lock on the two date fields, as the Mini
  // Profile does. Absent when nothing is locking the form.
  lockedHelperText?: ?string,
  // The household's move-in day (YYYY-MM-DD), the floor for both date
  // fields. Absent when the household has no move-in date yet.
  householdMoveInDay?: ?string,
  formatMessage: (descriptor: Object, values?: Object) => string,
  // The End Date rule uses this getter to read Start Date.
  getValues: (name?: string) => mixed,
}): Array<Object> => {
  const requiredMessage = formatMessage(messages.requiredError);
  const dateHelperText = lockedHelperText
    ? { helperText: lockedHelperText }
    : {};
  // Restrict calendar selection; validation also checks typed dates.
  const moveInMinDate = householdMoveInDay
    ? { minDate: moment(householdMoveInDay) }
    : {};

  // Omit date fields to preserve existing values.
  const startDateFields = hideDates
    ? []
    : [
        {
          variant: 'date',
          name: 'startDate',
          label: formatMessage(messages.startDate),
          disabled,
          ...dateHelperText,
          ...moveInMinDate,
          rules: {
            required: requiredMessage,
            validate: (value) => {
              if (isBlankDate(value)) return requiredMessage;
              if (isBeforeMoveIn(value, householdMoveInDay)) {
                return formatMessage(messages.startDateBeforeMoveIn);
              }
              return true;
            },
          },
          GridProps: FULL_WIDTH_ON_MOBILE_HALF_ON_DESKTOP,
        },
      ];

  const endDateFields =
    hideDates || hideEndDate
      ? []
      : [
          {
            variant: 'date',
            name: 'endDate',
            label: formatMessage(messages.endDate),
            placeholder: formatMessage(messages.optional),
            disabled,
            ...dateHelperText,
            // Set picker bounds and validate typed dates separately.
            maxDate: moment().endOf('day'),
            ...moveInMinDate,
            rules: {
              validate: (value) => {
                if (isBlankDate(value)) return true;
                if (isFutureDate(value)) {
                  return formatMessage(messages.endDateInFuture);
                }
                // Check Start Date before the move-in date.
                if (
                  isToDateNotSameOrAfterFromDate(
                    toCalendarDay(getValues('startDate')) ?? undefined,
                    toCalendarDay(value) ?? undefined,
                  )
                ) {
                  return formatMessage(messages.endDateBeforeStartDate);
                }
                if (isBeforeMoveIn(value, householdMoveInDay)) {
                  return formatMessage(messages.endDateBeforeMoveIn);
                }
                return true;
              },
            },
            watchFields: ['startDate'],
            GridProps: FULL_WIDTH_ON_MOBILE_HALF_ON_DESKTOP,
          },
        ];

  return [
    {
      variant: 'text',
      name: 'name',
      label: formatMessage(messages.name),
      disabled,
      rules: {
        required: requiredMessage,
        // Reject names that contain only spaces.
        validate: (value) =>
          String(value ?? '').trim().length > 0 || requiredMessage,
      },
      GridProps: FULL_WIDTH_ON_MOBILE_HALF_ON_DESKTOP,
    },
    {
      variant: 'singleSelect',
      name: 'petTypeId',
      label: formatMessage(messages.petType),
      options: petTypeOptions,
      placeholder: formatMessage(messages.choose),
      disabled,
      rules: {
        required: requiredMessage,
        validate: (value) => !isUnselected(value) || requiredMessage,
      },
      GridProps: FULL_WIDTH_ON_MOBILE_HALF_ON_DESKTOP,
    },
    {
      variant: 'singleSelect',
      name: 'petBreedId',
      label: formatMessage(messages.petBreed),
      // The selected pet type supplies these embedded breeds.
      options: breedOptions,
      placeholder: formatMessage(messages.choose),
      disabled,
      rules: {
        required: requiredMessage,
        validate: (value) => !isUnselected(value) || requiredMessage,
      },
      GridProps: FULL_WIDTH_ON_MOBILE_HALF_ON_DESKTOP,
    },
    {
      variant: 'number',
      name: 'weight',
      label: formatMessage(messages.weight),
      // The placeholder indicates whether Weight is required.
      placeholder: formatMessage(
        requiresWeight ? messages.required : messages.optional,
      ),
      disabled,
      rules: {
        validate: (value) => {
          const isEmpty = value === undefined || value === null || value === '';
          if (isEmpty) return requiresWeight ? requiredMessage : true;
          return Number(value) > 0 || formatMessage(messages.invalidWeight);
        },
      },
      GridProps: FULL_WIDTH_ON_MOBILE_HALF_ON_DESKTOP,
    },
    {
      variant: 'text',
      name: 'petTag',
      label: formatMessage(messages.petTag),
      disabled,
      rules: {
        maxLength: {
          value: PET_TAG_MAX_LENGTH,
          message: formatMessage(messages.petTagMaxLength),
        },
        pattern: {
          value: PET_TAG_PATTERN,
          message: formatMessage(messages.petTagPattern),
        },
      },
      GridProps: FULL_WIDTH_ON_MOBILE_HALF_ON_DESKTOP,
    },
    {
      // Require an explicit Yes or No selection.
      variant: 'singleSelect',
      name: 'isServiceAnimal',
      label: formatMessage(messages.isServiceAnimal),
      options: YES_NO_OPTIONS,
      placeholder: formatMessage(messages.choose),
      disabled,
      rules: {
        required: requiredMessage,
        validate: (value) => !isUnselected(value) || requiredMessage,
      },
      GridProps: FULL_WIDTH_ON_MOBILE_HALF_ON_DESKTOP,
    },
    ...startDateFields,
    ...endDateFields,
  ];
};
